<script lang="ts" setup>
import { useI18n } from "vue-i18n"
import { computed, useTemplateRef } from "vue"
import { useVModel } from "@vueuse/core"

import UtilisationProfileFormRow from "./UtilisationProfileFormRow.vue"
import { IrveFormProfileRow } from "@types"

const props = defineProps<{
    modelValue: IrveFormProfileRow[]
}>()

// Emits pour synchroniser les modifications avec le parent
const emit = defineEmits(["update:modelValue"])

// Tableau de références pour les composants enfants
const rowRefs = useTemplateRef<typeof UtilisationProfileFormRow>("rowRefs")

/**
 * Tableau de lignes de profil d'utilisation du bâtiment.
 * Chaque ligne représente un ensemble de jour et de périodes d'utilisation
 */
const modelValue = useVModel(props, "modelValue", emit, {
    deep: true,
    eventName: "update:modelValue",
    defaultValue: [],
    passive: true,
})

/**
 * Quand l'utilisateur clique sur le bouton "Ajouter", une nouvelle ligne est ajoutée
 * dans le tableau de profil d'utilisation
 */
function onClickAddProfileRow() {
    modelValue.value.push({ days: [], periods: [], foisonnement: undefined })
}

/**
 * Quand l'utilisateur clique sur le bouton "Supprimer", la ligne correspondante est supprimée
 * @param index - Index de la ligne à supprimer
 */
function onClickRemoveProfileRow(index: number) {
    modelValue.value.splice(index, 1)
}
/**
 * Récupère les périodes désactivées en fonction des autres lignes
 * @param index - Index de la ligne pour laquelle on veut récupérer les périodes désactivées
 */
const getDisabledPeriodsByRowIndex = computed(() => {
    if (!rowRefs.value?.length) {
        return {}
    }

    return rowRefs.value
        .map((row: InstanceType<typeof UtilisationProfileFormRow>) =>
            row.getDisabledPeriods()
        )
        .reduce(
            (
                acc: Record<string, { start: number; end: number }[]>,
                curr: Record<string, { start: number; end: number }[]>
            ) => {
                // on merge les jours
                Object.keys(curr).forEach((day) => {
                    if (!acc[day]) {
                        acc[day] = []
                    }
                    acc[day] = Array.from(new Set([...acc[day], ...curr[day]]))
                })

                return acc
            },
            {}
        )
})

defineExpose({
    validate: async () => {
        let valid = true

        if (!rowRefs.value?.length || !Array.isArray(rowRefs.value)) {
            return valid
        }

        for (const refElement of rowRefs.value) {
            if (refElement?.validate) {
                if (!(await refElement.validate())) {
                    valid = false
                }
            }
        }
        return valid
    },
})

// ==================================================================
// Lang
// ==================================================================
const { t } = useI18n({
    messages: {
        fr: {
            add: "Ajouter",
            validate: "Valider",
        },
    },
})
</script>
<template>
    <div class="utilisation-profile-form flex flex-col gap-10">
        <!-- Liste de lignes à renseigner -->
        <UtilisationProfileFormRow
            class="flex gap-2"
            v-for="(row, index) in modelValue"
            :key="index"
            :disabled-periods="getDisabledPeriodsByRowIndex"
            v-model:days="row.days"
            v-model:periods="row.periods"
            v-model:foisonnement="row.foisonnement"
            :show-button="index > 0"
            @click:remove="onClickRemoveProfileRow(index)"
            ref="rowRefs"
        >
        </UtilisationProfileFormRow>
        <!-- Bouton d'ajout -->
        <BaseButton
            class="self-center"
            :label="t('add')"
            icon="pi pi-plus"
            severity="secondary"
            @click="onClickAddProfileRow"
        ></BaseButton>
    </div>
</template>
